/**
 * ExpensesPage — M4b Ausgaben-Frontend
 *
 * Lists all expenses for the authenticated user (owner-scoped via M0-Rules).
 * Gated by feature flag REACT_APP_EXPENSES_ENABLED — fail-closed if absent.
 *
 * Displays: supplier, date, net/VAT-owed/gross, taxScheme label,
 * vatDeductible badge, and status (draft / booked).
 *
 * @module ExpensesPage
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconExpense, IconError, IconSuccess, IconRejected, IconUndo } from '../icons';

import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { RowActionsMenu, RowActionsMenuItem } from '../components/common/RowActionsMenu';
import { Expense } from '../types/expenseTypes';
import { getExpensesForUser } from '../api/expensesApi';
import { centsToEuros } from '../utils/moneyUtils';
import { formatDate } from '../utils/formatters';
import logger from '../utils/logger';
import ExpenseSummaryBar from '../components/expenses/ExpenseSummaryBar';
import ExpenseCorrectionModal from '../components/expenses/ExpenseCorrectionModal';
import TaxSchemeChip from '../components/expenses/TaxSchemeChip';
import { IncomeEntry } from '../utils/expenseSummary';
import { canUseExpenseCorrection } from '../config/expenseCorrectionAccess';
import { canUseVorsteuerCockpit, COCKPIT_FLAG } from '../config/vorsteuerCockpitAccess';
import { TIER_V2_FLAG } from '../config/entitlements';
import { indexCorrections, isCorrected, isCorrectionDoc } from '../utils/expenseCorrection';
import { loadIncomeForUser } from '../loaders/cockpitLoaders';
import FeatureTeaser from '../components/premium/FeatureTeaser';
import GraceCountdownBanner from '../components/premium/GraceCountdownBanner';

// ─── Feature flag guards ───────────────────────────────────────────────────────

const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';
// Das Vorsteuer-Cockpit-Hauptflag lebt jetzt in `config/vorsteuerCockpitAccess.ts`
// (`canUseVorsteuerCockpit` / `COCKPIT_FLAG`, Take-away-Gate PR-T5). Der frühere
// `COCKPIT_ENABLED`-Inline-Konstante wurde durch den per-User-Helfer ersetzt —
// bei `TIER_V2_FLAG=false` byte-identisch (Helfer liefert dann exakt `COCKPIT_FLAG`).
/**
 * Sub-Flag für die Brutto-Saldo-Kachel „Einnahmen − Ausgaben (brutto)"
 * (steuerlich heikelster Teil) — fail-closed Default aus. Env-Name bleibt aus
 * Kompatibilität (`…_EUER_ENABLED`), ist aber nirgends sichtbar (A2).
 */
const COCKPIT_BALANCE_ENABLED = process.env.REACT_APP_VORSTEUER_COCKPIT_EUER_ENABLED === 'true';

// `loadIncomeForUser` lebt seit M-UX-4.1 in `loaders/cockpitLoaders.ts` (SSOT,
// vom Cockpit-Dashboard mitgenutzt) — kein Verhaltensdelta, reiner Import-Umzug.

// ─── PR-T6 (D7, §3.3-SONDERFALL) — Vorsteuer-Cockpit-Upsell-Teaser ─────────────

/**
 * Upsell-Teaser für die Vorsteuer-Cockpit-Summenleiste (PR-T5 Take-away-Gate).
 *
 * Ziel-Tier **Ultra** (Korrektur 2026-07-29): `canUseVorsteuerCockpit` verlangt
 * seit dem Marketing-Relaunch `resolveTier(sellerData) === 'ultra'`
 * (`vorsteuerCockpitAccess.ts`), der Teaser bewarb aber weiterhin „Premium" —
 * ein Premium-Kauf hätte das Feature nicht freigeschaltet (§5 UWG). Badge/CTA/
 * Ziel-Route folgen jetzt dem Bank-Abgleich-Muster
 * (`BankReconciliationPage.tsx`).
 *
 * SONDERFALL (behebt T5-🟡): NUR die Summenleiste ist tier-gegatet — die
 * Ausgaben-/Beleg-LISTE bleibt für alle sichtbar (kein Premium-Feature). Der
 * Teaser tritt an die Stelle der `ExpenseSummaryBar`, NICHT an die Stelle der
 * ganzen Seite. Nur erreichbar, wenn das Cockpit-Feature ausgeliefert ist
 * (`COCKPIT_FLAG`) UND das Take-away-Master-Flag aktiv ist (`TIER_V2_FLAG`) —
 * s. Render-Gate unten (bei einem der beiden Flags aus bleibt die Summenleiste
 * einfach unsichtbar, wie bisher, statt einen Teaser für ein nicht
 * ausgeliefertes Feature zu zeigen).
 *
 * `layout="inline"` (PR-T6-F1, Reviewer-Fund F1): `FeatureTeaser`s Default
 * (`layout="page"`) ist ganzseitig (`min-h-screen`) und dafür korrekt bei
 * Bank/DATEV/OpenItems (Teaser = gesamte Seite). Hier ersetzt der Teaser aber
 * NUR die `ExpenseSummaryBar`-Position — die Beleg-Liste bleibt darunter
 * sichtbarer Primärinhalt. `layout="inline"` rendert ein kompaktes
 * horizontales Band statt Vollhöhe, damit die Liste nicht unter den Fold
 * geschoben wird.
 */
const VorsteuerCockpitTeaser: React.FC = () => (
  <FeatureTeaser
    layout="inline"
    icon={<IconExpense size={30} aria-hidden />}
    badgeKey="vorsteuerCockpit.teaserBadge"
    badgeDefault="Ultra-Funktion"
    titleKey="vorsteuerCockpit.teaserTitle"
    titleDefault="Vorsteuer-Cockpit"
    descriptionKey="vorsteuerCockpit.teaserDescription"
    descriptionDefault="Sehen Sie auf einen Blick, wie viel Vorsteuer Sie aus Ihren Ausgaben zurückholen können — automatisch berechnet aus Ihren gebuchten Belegen."
    highlights={[
      {
        labelKey: 'vorsteuerCockpit.teaserHighlight1',
        defaultLabel: 'Vorsteuer-Summe automatisch berechnet',
      },
      { labelKey: 'vorsteuerCockpit.teaserHighlight2', defaultLabel: 'Zeitraum-Filter für die Voranmeldung' },
      {
        labelKey: 'vorsteuerCockpit.teaserHighlight3',
        defaultLabel: 'Immer aktuell durch gebuchte Belege',
      },
    ]}
    ctaKey="vorsteuerCockpit.teaserCta"
    ctaDefault="Jetzt auf Ultra upgraden"
    promotionId="feature_teaser_vorsteuer_cockpit"
    creativeSlot="page"
  />
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

// ─── ExpensesPage ─────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'draft' | 'booked';

/**
 * Lists all expenses for the currently authenticated user.
 *
 * Fail-closed: if REACT_APP_EXPENSES_ENABLED is not 'true' this page
 * renders nothing (the route itself is also conditionally registered in App.tsx).
 */
const ExpensesPage: React.FC = () => {
  const { t } = useTranslation('expenses');
  const { currentUser, effectiveScopeUid, sellerData } = useAuth();

  // M-G4 (architecture-grace-window.md §6.2/§8.1): GraceCountdownBanner nur im
  // Owner-Kontext — im Mandanten-Modus bezieht sich das Fenster auf das EIGENE
  // Abo des StB, dort wäre es irreführend. `useIsReadOnly()` lebt bisher nur in
  // `ExpenseRow` (Korrektur-Button-Gate); für den Banner auf Page-Ebene wird der
  // Hook hier zusätzlich aufgerufen (kein Hoisting/Entfernen der ExpenseRow-Nutzung).
  const isReadOnly = useIsReadOnly();

  // M-A1 (§6.3): Advisor-im-Mandanten-Modus wird nie owner-tier-gegatet. Signal
  // ist der Scope-Vergleich (VERBATIM `DatevExportPage.tsx:106`), NICHT
  // `useIsReadOnly()`/`useIsAdvisorContext()` — nur der Fremd-Tenant-Scope zählt,
  // der eigene Kanzlei-Scope bleibt Owner-Pfad.
  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;

  // Vorsteuer-Cockpit-Zugriff (Take-away-Gate PR-T5). Bei TIER_V2_FLAG=false
  // liefert der Helfer exakt `COCKPIT_FLAG` ⇒ byte-identisch zum bisherigen
  // `COCKPIT_ENABLED`. Einmal pro Render berechnet, unten wiederverwendet.
  const canCockpit = canUseVorsteuerCockpit(sellerData, !isOwnScope);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Expense-Storno (M-EC-5) — beta-gated correction assistant. Gate is keyed
  // on the tenant scope (effectiveScopeUid), matching the established pattern
  // for DATEV-Export/Belege (canUseDatevExport/canUseDatevBelege) — the
  // feature is available per ACCOUNT, not per logged-in identity, so an
  // advisor viewing a beta mandant's data also sees it (read-only, see below).
  const [correctionExpense, setCorrectionExpense] = useState<Expense | null>(null);

  // Cockpit income (Brutto-Saldo) — only loaded when cockpit + balance sub-flag on.
  const [income, setIncome] = useState<IncomeEntry[] | null>(null);
  const [incomeLoading, setIncomeLoading] = useState(false);

  // ── Load expenses ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!EXPENSES_ENABLED || !currentUser) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getExpensesForUser(effectiveScopeUid);
        if (!cancelled) {
          setExpenses(data);
        }
      } catch (err) {
        if (!cancelled) {
          logger.error('ExpensesPage: load failed', err);
          setError(String(err instanceof Error ? err.message : err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser, effectiveScopeUid]);

  // ── Load income for the cockpit's gross-balance figure (flag-gated, fail-closed) ──
  useEffect(() => {
    if (!EXPENSES_ENABLED || !canCockpit || !COCKPIT_BALANCE_ENABLED || !currentUser) {
      // Sub-Flag aus ⇒ income bleibt null ⇒ Brutto-Saldo-Kachel ausgeblendet.
      setIncome(null);
      setIncomeLoading(false);
      return;
    }

    let cancelled = false;
    setIncomeLoading(true);

    loadIncomeForUser(effectiveScopeUid)
      .then((data) => {
        if (!cancelled) setIncome(data);
      })
      .catch((err) => {
        // Fail-closed: kein Page-Crash, Kachel verschwindet (income=null).
        if (!cancelled) {
          logger.error('ExpensesPage: income load failed', err);
          setIncome(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIncomeLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentUser, effectiveScopeUid, canCockpit]);

  // Expense-Storno beta-gate + korrigiert-Index (before the early return below
  // to keep hook order stable across renders — rules-of-hooks).
  const correctionBetaGate = canUseExpenseCorrection(effectiveScopeUid);
  const correctionIdx = useMemo(() => indexCorrections(expenses), [expenses]);

  /** Re-fetches the expense list (used by the error-retry button and after a booked correction). */
  const reloadExpenses = () => {
    if (!currentUser) return;
    setError(null);
    setLoading(true);
    getExpensesForUser(effectiveScopeUid)
      .then(setExpenses)
      .catch((err) => setError(String(err instanceof Error ? err.message : err)))
      .finally(() => setLoading(false));
  };

  // Feature flag not enabled — render nothing (fail-closed)
  if (!EXPENSES_ENABLED) return null;

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = expenses.filter((e) =>
    statusFilter === 'all' ? true : e.status === statusFilter,
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-screen-2xl">
      <div>

        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
            <IconExpense className="text-accent-ink" size={24} />
            {t('pageTitle')}
          </h1>
          <p className="text-muted text-sm mt-1">{t('pageSubtitle')}</p>
        </div>

        {/* Vorsteuer-Cockpit (flag-gated, fail-closed). Renders even with 0 expenses
            (shows 0,00 €) so the dashboard stays consistent for new users.
            PR-T6 (D7, §3.3-SONDERFALL): bei aktivem Cockpit-Feature (COCKPIT_FLAG)
            UND aktivem Take-away-Master-Flag (TIER_V2_FLAG), aber fehlender
            Berechtigung, tritt EIN Teaser an die Stelle der Summenleiste — die
            Ausgaben-LISTE darunter bleibt davon komplett unberührt (immer sichtbar,
            kein Premium-Feature). Bei COCKPIT_FLAG aus ODER TIER_V2_FLAG aus bleibt
            es beim bisherigen Verhalten (Summenleiste einfach unsichtbar, kein
            Teaser für ein nicht ausgeliefertes Feature). */}
        {!loading && !error && (
          canCockpit ? (
            <>
              {/* Grace-Window-Countdown (M-G4, architecture-grace-window.md §8.1):
                  direkt oberhalb der Summenleiste, NUR im Owner-Kontext
                  (`!isReadOnly` — im Mandanten-Modus bezieht sich das Fenster auf
                  das EIGENE Abo des StB, dort wäre es irreführend, §6.2). Die
                  Komponente selbst ist inert (rendert `null` ohne aktives
                  Grace-Fenster — §9 Null-Delta). */}
              {!isReadOnly && (
                <GraceCountdownBanner
                  sellerData={sellerData}
                  feature="vorsteuerCockpit"
                  featureNameKey="graceBanner.featureVorsteuerCockpit"
                  featureNameDefault="das Vorsteuer-Cockpit"
                  promotionId="grace_countdown_vorsteuer_cockpit"
                />
              )}
              <ExpenseSummaryBar
                expenses={expenses}
                income={income}
                incomeLoading={incomeLoading}
              />
            </>
          ) : (
            COCKPIT_FLAG && TIER_V2_FLAG && <VorsteuerCockpitTeaser />
          )
        )}

        {/* Status filter */}
        {!loading && !error && expenses.length > 0 && (
          <div className="flex gap-2 mb-5">
            {(['all', 'draft', 'booked'] as StatusFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  statusFilter === f
                    ? 'bg-indigo-600 text-white'
                    : 'bg-surface-raised text-muted hover:text-primary border border-hairline'
                }`}
              >
                {t(`list.filter.${f}`)}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16 text-muted text-sm">
            {t('list.loading')}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-6 text-center">
            <IconError className="text-[var(--tone-danger-fg)] mx-auto mb-3" size={30} />
            <p className="text-[var(--tone-danger-fg)] font-medium">{t('list.error.title')}</p>
            <p className="text-[var(--tone-danger-fg)] text-xs mt-1 font-mono">{error}</p>
            <button
              onClick={reloadExpenses}
              className="mt-4 px-4 py-2 text-sm bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              {t('list.error.retry')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && expenses.length === 0 && (
          <div className="text-center py-16">
            <IconExpense className="text-gray-600 mx-auto mb-4" size={48} />
            <p className="text-muted font-medium">{t('list.empty')}</p>
            <p className="text-faint text-sm mt-1">{t('list.emptyHint')}</p>
          </div>
        )}

        {/* No results for current filter */}
        {!loading && !error && expenses.length > 0 && filtered.length === 0 && (
          <div className="text-center py-12 text-faint text-sm">
            {t('list.noMatch')}
          </div>
        )}

        {/* Expense table */}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-raised text-muted text-xs uppercase tracking-wide">
                  <th className="px-4 py-2 text-left font-medium">{t('list.columns.supplier')}</th>
                  <th className="px-4 py-2 text-left font-medium">{t('list.columns.date')}</th>
                  <th className="hidden xl:table-cell px-4 py-2 text-right font-medium">{t('list.columns.net')}</th>
                  <th className="px-4 py-2 text-right font-medium">{t('list.columns.vat')}</th>
                  <th className="px-4 py-2 text-right font-medium">{t('list.columns.gross')}</th>
                  <th className="hidden lg:table-cell px-4 py-2 text-left font-medium">{t('list.columns.scheme')}</th>
                  <th className="px-4 py-2 text-center font-medium">{t('list.columns.deductible')}</th>
                  <th className="px-4 py-2 text-center font-medium">{t('list.columns.status')}</th>
                  {correctionBetaGate && (
                    <th className="sticky right-0 z-10 bg-surface-raised border-l border-default px-4 py-2 text-center font-medium">{t('list.columns.actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border-hairline)]">
                {filtered.map((expense) => (
                  <ExpenseRow
                    key={expense.id}
                    expense={expense}
                    t={t}
                    correctionBetaGate={correctionBetaGate}
                    correctionIdx={correctionIdx}
                    onCorrect={setCorrectionExpense}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Expense-Storno (M-EC-5) — guided correction assistant, beta-gated. */}
        {correctionExpense && (
          <ExpenseCorrectionModal
            open={!!correctionExpense}
            expense={correctionExpense}
            onClose={() => setCorrectionExpense(null)}
            onCorrected={() => {
              setCorrectionExpense(null);
              reloadExpenses();
            }}
          />
        )}
      </div>
    </div>
  );
};

// ─── ExpenseRow ───────────────────────────────────────────────────────────────

interface ExpenseRowProps {
  expense: Expense;
  t: ReturnType<typeof useTranslation<'expenses'>>['t'];
  /** Expense-Storno beta-gate (M-EC-5) — hides badges/button entirely when off. */
  correctionBetaGate: boolean;
  /** Original-expenseId → correction doc, from `indexCorrections`. */
  correctionIdx: Map<string, Expense>;
  /** Opens the correction modal for this expense (only relevant when correctable). */
  onCorrect: (expense: Expense) => void;
}

/**
 * A single row in the expenses table.
 */
const ExpenseRow: React.FC<ExpenseRowProps> = ({
  expense,
  t,
  correctionBetaGate,
  correctionIdx,
  onCorrect,
}) => {
  // M11b-3 defense-in-depth — advisor (read-only) never sees the correction button.
  const isReadOnly = useIsReadOnly();
  const isCorrection = isCorrectionDoc(expense);
  const correction = !isCorrection ? correctionIdx.get(expense.id) : undefined;
  const alreadyCorrected = !isCorrection && isCorrected(expense.id, correctionIdx);
  const canCorrect =
    correctionBetaGate &&
    !isReadOnly &&
    expense.status === 'booked' &&
    !isCorrection &&
    !alreadyCorrected;

  // M-V4-E (architecture-ux-v4.md §5) — the "Korrigieren" RowAction moves
  // into the shared ⋯-RowActionsMenu (common/RowActionsMenu, M-V4-A). Empty
  // items array ⇒ RowActionsMenu renders `null` itself (no dead ⋯-trigger on
  // rows that aren't correctable) — same contract as OpenItemsPage M-V4-C.
  const rowActionsItems: RowActionsMenuItem[] = canCorrect
    ? [
        {
          id: 'correct',
          label: t('correction.button'),
          icon: <IconUndo />,
          variant: 'danger',
          onSelect: () => onCorrect(expense),
        },
      ]
    : [];

  return (
    <tr className="bg-[rgb(var(--surface-raised-rgb)/0.5)] hover:bg-surface-raised transition-colors">
      {/* Supplier */}
      <td className="px-4 py-2">
        <p className="text-primary font-medium truncate max-w-[160px]">{expense.supplierName}</p>
        {expense.invoiceNumber && (
          <p className="text-faint text-xs font-mono truncate">{expense.invoiceNumber}</p>
        )}
      </td>

      {/* Date */}
      <td className="px-4 py-2 text-muted text-xs whitespace-nowrap">
        {formatDate(expense.expenseDate)}
      </td>

      {/* Net */}
      <td className="hidden xl:table-cell px-4 py-2 text-right text-secondary font-mono text-xs tabular-nums whitespace-nowrap">
        {formatCents(expense.netCents)}
      </td>

      {/* VAT owed */}
      <td className="px-4 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap">
        <span className={expense.vatOwedCents > 0 ? 'text-secondary' : 'text-gray-600'}>
          {formatCents(expense.vatOwedCents)}
        </span>
      </td>

      {/* Gross */}
      <td className="px-4 py-2 text-right text-primary font-mono text-xs font-medium tabular-nums whitespace-nowrap">
        {formatCents(expense.grossCents)}
      </td>

      {/* Tax scheme chip (M-B, StatusChip-based — replaces the long-text pill;
          M-V4-E: vatRate/vatBreakdown passed through so the chip can derive
          the ACTUAL rate for `standard` instead of a static "Regel" text). */}
      <td className="hidden lg:table-cell px-4 py-2">
        <TaxSchemeChip scheme={expense.taxScheme} vatRate={expense.vatRate} vatBreakdown={expense.vatBreakdown} />
      </td>

      {/* vatDeductible badge */}
      <td className="px-4 py-2 text-center">
        {expense.vatDeductible ? (
          <span className="inline-block" title={t('vatDeductible')}>
            <IconSuccess className="text-[var(--tone-success-fg)] mx-auto" size={14} />
          </span>
        ) : (
          <span className="inline-block" title={t('vatNotDeductible')}>
            <IconRejected className="text-gray-600 mx-auto" size={14} />
          </span>
        )}
      </td>

      {/* Status badge */}
      <td className="px-4 py-2 text-center">
        <span
          className={`inline-block text-xs px-2 py-0.5 rounded-md font-medium ${
            expense.status === 'booked'
              ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]'
              : 'bg-surface-inset text-muted'
          }`}
        >
          {t(`status.${expense.status}`)}
        </span>
      </td>

      {/* Expense-Storno (M-EC-5) — badge (original/correction) or "Korrigieren" button.
          Sticky right so the Storno action is never the column that scrolls off. */}
      {correctionBetaGate && (
        <td className="sticky right-0 z-10 bg-surface-raised border-l border-default px-4 py-2 text-center whitespace-nowrap">
          {isCorrection && expense.correctionType === 'storno' && (
            <span className="inline-block text-xs px-2 py-0.5 rounded-md font-medium bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)]">
              {t('correction.badge.correctionDocStorno')}
            </span>
          )}
          {isCorrection && expense.correctionType === 'privatentnahme' && (
            <span className="inline-block text-xs px-2 py-0.5 rounded-md font-medium bg-[var(--tone-special-bg)] text-[var(--tone-special-fg)]">
              {t('correction.badge.correctionDocPrivatentnahme')}
            </span>
          )}
          {!isCorrection && correction?.correctionType === 'storno' && (
            <span className="inline-block text-xs px-2 py-0.5 rounded-md font-medium bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)]">
              {t('correction.badge.originalStorno')}
            </span>
          )}
          {!isCorrection && correction?.correctionType === 'privatentnahme' && (
            <span className="inline-block text-xs px-2 py-0.5 rounded-md font-medium bg-[var(--tone-special-bg)] text-[var(--tone-special-fg)]">
              {t('correction.badge.originalPrivatentnahme')}
            </span>
          )}
          <RowActionsMenu items={rowActionsItems} data-testid="expense-row-actions-menu" />
        </td>
      )}
    </tr>
  );
};

export default ExpensesPage;
